import React, { useEffect } from 'react';
import { Command } from 'cmdk';
import { Search, LayoutDashboard, Settings, Image as ImageIcon, Calendar, FileText, X, Briefcase } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function CommandPalette({ 
  open, 
  setOpen, 
  setActiveTab 
}: { 
  open: boolean; 
  setOpen: (open: boolean) => void;
  setActiveTab: (tab: 'dashboard' | 'brand' | 'plans' | 'settings') => void;
}) {
  const navigate = useNavigate();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(!open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [open, setOpen]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-gray-900/50 backdrop-blur-sm flex items-start justify-center pt-[20vh]" onClick={() => setOpen(false)}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-gray-100" onClick={e => e.stopPropagation()}>
        <Command className="w-full" label="Command Menu">
          <div className="flex items-center border-b border-gray-100 px-4">
            <Search className="w-5 h-5 text-gray-400" />
            <Command.Input 
              autoFocus
              placeholder="Type a command or search..." 
              className="w-full p-4 outline-none text-gray-900 placeholder:text-gray-400 bg-transparent"
            />
            <button onClick={() => setOpen(false)} className="p-1 text-gray-400 hover:text-gray-600 rounded-md">
              <X className="w-4 h-4" />
            </button>
          </div>

          <Command.List className="max-h-[300px] overflow-y-auto p-2">
            <Command.Empty className="p-4 text-center text-gray-500 text-sm">No results found.</Command.Empty>

            <Command.Group heading="Navigation" className="text-xs font-bold text-gray-400 uppercase tracking-widest px-2 py-2">
              <Command.Item 
                onSelect={() => { navigate('/dashboard'); setActiveTab('dashboard'); setOpen(false); }}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-indigo-50 hover:text-indigo-700 cursor-pointer text-gray-700 text-sm font-medium transition-colors"
              >
                <LayoutDashboard className="w-4 h-4" />
                Dashboard
              </Command.Item>
              <Command.Item 
                onSelect={() => { navigate('/dashboard'); setActiveTab('brand'); setOpen(false); }}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-indigo-50 hover:text-indigo-700 cursor-pointer text-gray-700 text-sm font-medium transition-colors"
              >
                <ImageIcon className="w-4 h-4" />
                Brand Profile
              </Command.Item>
              <Command.Item 
                onSelect={() => { navigate('/dashboard'); setActiveTab('plans'); setOpen(false); }}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-indigo-50 hover:text-indigo-700 cursor-pointer text-gray-700 text-sm font-medium transition-colors"
              >
                <Calendar className="w-4 h-4" />
                Content Plans
              </Command.Item>
              <Command.Item 
                onSelect={() => { navigate('/dashboard'); setActiveTab('settings'); setOpen(false); }}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-indigo-50 hover:text-indigo-700 cursor-pointer text-gray-700 text-sm font-medium transition-colors"
              >
                <Settings className="w-4 h-4" />
                Settings
              </Command.Item>
            </Command.Group>

            <Command.Group heading="Resources" className="text-xs font-bold text-gray-400 uppercase tracking-widest px-2 py-2 mt-2">
              <Command.Item 
                onSelect={() => { navigate('/showcase'); setOpen(false); }}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-indigo-50 hover:text-indigo-700 cursor-pointer text-gray-700 text-sm font-medium transition-colors"
              >
                <Briefcase className="w-4 h-4" />
                Showcase
              </Command.Item>
              <Command.Item 
                onSelect={() => { navigate('/blog'); setOpen(false); }}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-indigo-50 hover:text-indigo-700 cursor-pointer text-gray-700 text-sm font-medium transition-colors"
              >
                <FileText className="w-4 h-4" />
                Read the Blog
              </Command.Item>
            </Command.Group>
          </Command.List>
        </Command>
      </div>
    </div>
  );
}
